import { useContext } from 'react';
import useRecaptcha from 'hooks/useRecaptcha';
import i18n from 'utils/dynamic/i18n';
import { DynamicEventResponse } from 'ts/dynamic/interfaces/DynamicResponse';
import * as idb from 'utils/shared/idbUrl';
import { hotelsTripadvisor, loadGoogleByDynamicProposal } from 'services/dynamic';
import { MainContext } from 'contexts/MainContext';

export const useDynamicProposal = () => {
  const { proposal, setProposal } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha();

  const toggleDynamicProposal = async (data: DynamicEventResponse) => {
    if (!proposal || !setProposal) return;
    const existingProposalIndex = proposal.dynamic.findIndex(
      (proposal) => proposal.searchToken === data.searchToken,
    );

    if (existingProposalIndex !== -1) {
      const updatedProposals = proposal.dynamic.filter(
        (_, index) => index !== existingProposalIndex,
      );
      setProposal({ ...proposal, dynamic: updatedProposals });
    } else {
      const newProposal: any = {
        searchToken: data.searchToken,
        price: data.packageInfo.packageTotalPrice || 0,
        currency: data.packageInfo.currencyCode,
        displayPrice: true,
      };

      if (data.dynamicHotel) {
        newProposal.hotel = {
          ...data.dynamicHotel?.selectedHotel!,
          searchToken: data.dynamicHotel?.searchToken,
        };
      }

      if (data.dynamicFlight) {
        newProposal.flight = data.dynamicFlight?.selectedFlight as any;
      }

      if (data.dynamicAttraction) {
        const tickets = data.dynamicAttraction?.tickets.filter(
          (item) => item.ticket.itemNumber === data.dynamicAttraction?.id,
        );
        newProposal.attraction = { ...data.dynamicAttraction, tickets } as any;
      }
      if (data.dynamicAttraction && !data.dynamicHotel) {
        const currency = sessionStorage.getItem('searchAttractionCurrency') || 'usd';
        newProposal.currency = currency;
        newProposal.price = (data.dynamicAttraction?.price?.gross as any)[currency] || 0;
      }

      (async () => {
        const token = await handleRecapcha();
        if (token) {
          const googleData = await loadGoogleByDynamicProposal(newProposal, i18n.language, token);

          const prevProposal = await idb.getProposal('proposal');
          const updatedDynamic = prevProposal.dynamic.map((dynamic: any) =>
            dynamic.searchToken === newProposal.searchToken
              ? {
                  ...dynamic,
                  googleData,
                  googleChecked: {
                    imagesIndexes: dynamic.googleChecked?.imagesIndexes ?? [],
                    reviews:
                      dynamic.googleChecked?.reviews ??
                      !!googleData?.serpResults?.serpHotel?.reviews,
                    language: i18n.language,
                  },
                }
              : dynamic,
          );
          const updatedProposal = {
            ...prevProposal,
            dynamic: updatedDynamic,
          };

          setProposal(updatedProposal);
        }
      })();

      (async () => {
        const tripAdvisorData = await hotelsTripadvisor({
          bpid: newProposal.hotel?.item.bpid || '',
          lang: i18n.language,
        });

        setProposal(((prev: any) => {
          const updatedDynamic = prev.dynamic.map((dynamic: any) =>
            dynamic.searchToken === newProposal.searchToken
              ? {
                  ...dynamic,
                  tripAdvisorData,
                  tripAdvisorChecked: {
                    details: dynamic.tripAdvisorChecked?.details ?? false,
                    reviews: dynamic.tripAdvisorChecked?.reviews ?? false,
                    amenities: dynamic.tripAdvisorChecked?.amenities ?? false,
                    language: i18n.language,
                  },
                }
              : dynamic,
          );
          return {
            ...prev,
            dynamic: updatedDynamic,
          };
        }) as any);
      })();

      setProposal({ ...proposal, dynamic: [...proposal.dynamic, newProposal] });
    }
  };

  const isDynamicInProposal = (data: DynamicEventResponse) => {
    if (!proposal) return false;
    return proposal.dynamic.some((proposal) => proposal.searchToken === data.searchToken);
  };

  return {
    toggleDynamicProposal,
    isDynamicInProposal,
  };
};
